/* eslint-disable max-lines-per-function */
import { FC, useEffect, useRef } from "react";
import Select from "@aleaplay/web-components/ui/Select";

import { FormChildProps } from "@/v1/components/atoms/FormChild";
import { ISearchParams } from "@/v1/types/global";
import debounce from "@/v1/utils/debounce";
import { IBaseResult } from "@/v2/types/global/BaseResult.interface";
import { UseQueryParamsType } from "@/v2/types/global/UseQueryParams.type";
import { UseQueryType } from "@/v2/types/global/UseQueryType.type";
import { Sort } from "@/v2/utils/constants";

export interface QuerySelectProps extends FormChildProps {
  useQuery: UseQueryType;
  useQueryParams?: UseQueryParamsType;
  returnFullValue?: boolean;
  params?: ISearchParams;
  defaultParams?: ISearchParams;
  defaultValueId?: number;
  showIdOnOptions?: boolean;
  searchKey?: string;
  label?: string;
  onChange?: (...arg: any) => void;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  placeholder?: string;
  isMultiple?: boolean;
  disabled?: boolean;
}

export const QuerySelect: FC<QuerySelectProps> = ({
  useQuery,
  useQueryParams,
  returnFullValue,
  value,
  params,
  defaultParams,
  defaultValueId,
  searchKey = "name",
  showIdOnOptions = true,
  onChange,
  isMultiple,
  ...props
}) => {
  const { data } = useQuery();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const setQueryParams = useQueryParams && useQueryParams((state) => state.set); //NOSONAR

  const nameRef = useRef("");
  const resetRef = () => (nameRef.current = "");

  const results = data?.results as IBaseResult[];

  useEffect(() => {
    setQueryParams?.({
      [searchKey]: nameRef.current,
      sort: ["name", Sort.ASC],
      size: undefined,
      ...(defaultParams || {}),
      ...(params || {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setQueryParams, params]);

  useEffect(() => {
    setQueryParams?.({
      id: defaultValueId,
    });
  }, [setQueryParams, defaultValueId]);

  const handleOnChange = (newValue: number) => {
    if (onChange) {
      onChange(newValue);
    }
    resetRef();
  };

  const handleOnClose = () => {
    setQueryParams?.({ [searchKey]: nameRef.current, id: defaultValueId });
    resetRef();
  };

  const getType = () => {
    let type = setQueryParams ? "autocomplete" : "single";
    if (isMultiple) {
      type = "multiple";
    }
    return type;
  };

  return (
    <Select
      type={getType()}
      onSearch={
        setQueryParams
          ? debounce((name: string) => {
            nameRef.current = name;
            setQueryParams?.({ [searchKey]: name, id: undefined });
          }, 300)
          : undefined
      }
      onClose={handleOnClose}
      onOpen={() => setQueryParams?.({ [searchKey]: "", id: undefined })}
      onChange={handleOnChange}
      {...props}
      value={returnFullValue ? results?.find((result) => result.id === value) : value}
    >
      {results?.map((result) => (
        <Select.Option key={result.id} value={returnFullValue ? result : result.id}>
          {showIdOnOptions ? `${result.id} - ${result.name}` : result.name}
        </Select.Option>
      ))}
    </Select>
  );
};
